import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import {
  PrismButton,
  PrismInput,
  PrismMessage,
  PrismModal,
} from '@mylighthouse/prism-ember';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import type { CurrencyConversionExchangeRatesProfile } from 'lumen/models';
import SilentTaskError, {
  getErrorMessage,
} from 'lumen/utils/silent-task-error';

import styles from './delete-profile.module.css';

interface SettingsCurrencyConversionModalsDeleteProfileSignature {
  Args: {
    exchangeRatesProfile: CurrencyConversionExchangeRatesProfile;
    onClose: () => void;
  };
}

export default class SettingsCurrencyConversionModalsDeleteProfile extends Component<SettingsCurrencyConversionModalsDeleteProfileSignature> {
  @service declare currencyConversion: Services['currency-conversion'];
  @service declare errorTracking: Services['error-tracking'];
  @service declare intl: Services['intl'];
  @service declare toast: Services['toast'];

  @tracked inputValue = '';

  deleteProfileTask = task(async (): Promise<void> => {
    try {
      await this.currencyConversion.deleteExchangeRatesProfile(
        this.args.exchangeRatesProfile,
      );
    } catch (error) {
      this.toast.error(this.intl.t('general.error_short'), { showIcon: true });
      this.errorTracking.captureException(error);
      throw new SilentTaskError(getErrorMessage(error));
    }
    this.args.onClose();
  });

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isDeleteButtonDisabled() {
    return (
      this.inputValue.toLowerCase() !== this.profileTitle?.toLowerCase() ||
      this.isDeleting
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isDeleting() {
    return this.deleteProfileTask.isRunning;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get profileTitle() {
    return this.args.exchangeRatesProfile.title;
  }

  @action handleDeleteProfile(): void {
    void this.deleteProfileTask.perform();
  }

  @action onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !this.isDeleteButtonDisabled) {
      void this.handleDeleteProfile();
    }
  }

  @action setInputValue(event: Event): void {
    this.inputValue = (event.target as HTMLInputElement).value;
  }

  <template>
    <PrismModal
      @isCloseDisabled={{this.isDeleting}}
      @onClose={{@onClose}}
      class={{styles.currency-conversion-confirmation-modal}}
    >
      <:default>
        <div class="flex flex-col items-center justify-center px-500">
          <PrismMessage
            @description={{t
              "settings.currency_conversion.confirm.delete_profile.description"
            }}
            @illustration="100x100_warning"
            @title={{t
              "settings.currency_conversion.confirm.delete_profile.title"
              title=this.profileTitle
            }}
          />

          <PrismInput
            @autocomplete="off"
            @autofocus={{true}}
            @label={{t
              "settings.currency_conversion.confirm.delete_profile.type_to_confirm"
              confirmationText=this.profileTitle
            }}
            @name="confirm"
            @onInput={{this.setInputValue}}
            @onKeyDown={{this.onKeyDown}}
            @type="text"
            @value={{this.inputValue}}
          />
        </div>
      </:default>

      <:footer>
        <div class="m-300 flex justify-end gap-300">
          <PrismButton
            @buttonType="secondary"
            @isDisabled={{this.isDeleting}}
            @label={{t "general.cancel"}}
            @onClick={{@onClose}}
          />

          <PrismButton
            @buttonType="critical"
            @isDisabled={{this.isDeleteButtonDisabled}}
            @isLoading={{this.isDeleting}}
            @label={{t "settings.currency_conversion.action.delete_profile"}}
            @onClick={{this.handleDeleteProfile}}
          />
        </div>
      </:footer>
    </PrismModal>
  </template>
}
